import React, { useState, useEffect } from 'react';
import { Plus, Users, Building2, Trash2, AlertCircle } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { useAuth } from '@/hooks/useAuth';
import { Helmet } from 'react-helmet-async';

interface Organisation {
    id: number;
    name: string;
    email: string;
    city?: string;
    sector?: string;
    subscription_tier: string;
    max_users: number;
    created_at: string;
}

export default function OrganisationsPage() {
    const { user } = useAuth();
    const [organisations, setOrganisations] = useState<Organisation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        city: '',
        postcode: '',
        sector: '',
        org_type: 'enterprise',
        max_users: 5,
        subscription_tier: 'starter'
    });

    useEffect(() => {
        if (user) {
            loadOrganisations();
        }
    }, [user]);

    const loadOrganisations = async () => {
        try {
            setLoading(true);
            setError(null);
            const { getApiUrl } = await import('../config');
            const response = await fetch(getApiUrl('/organisations'), {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setOrganisations(Array.isArray(data) ? data : data.data || []);
        } catch (error) {
            console.error('Load organisations error:', error);
            setError('Failed to load organisations. Please try again.');
            setOrganisations([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (orgId: number) => {
        if (!confirm('Remove this organisation? This cannot be undone.')) return;
        try {
            const { getApiUrl } = await import('../config');
            const response = await fetch(getApiUrl(`/organisations/${orgId}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                setOrganisations(prev => prev.filter(o => o.id !== orgId));
            } else {
                const data = await response.json();
                alert(data.error || 'Failed to remove organisation');
            }
        } catch (error) {
            console.error('Delete organisation error:', error);
            alert('Error removing organisation.');
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'max_users' ? parseInt(value) : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                city: formData.city,
                postcode: formData.postcode,
                sector: formData.sector,
                org_type: formData.org_type,
                max_users: formData.max_users,
                subscription_tier: formData.subscription_tier
            };

            const { getApiUrl } = await import('../config');
            const response = await fetch(getApiUrl('/organisations'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(payload)
            });
            
            const data = await response.json();
            if (response.ok) {
                alert('Organisation created successfully!');
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    city: '',
                    postcode: '',
                    sector: '',
                    org_type: 'enterprise',
                    max_users: 5,
                    subscription_tier: 'starter'
                });
                setShowForm(false);
                loadOrganisations();
            } else {
                alert(data.error || 'Failed to create organisation');
                setError(data.error || 'Failed to create organisation');
            }
        } catch (error) {
            console.error('Create organisation error:', error);
            setError('Error creating organisation. Please try again.');
            alert('Error creating organisation. Check console for details.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <Helmet>
                    <title>Organisations | VireWire</title>
                </Helmet>
                <div className="flex justify-center items-center h-screen">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <Helmet>
                <title>Organisations | VireWire</title>
            </Helmet>
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-8">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                                <Building2 className="h-8 w-8" />
                                Organisations
                            </h1>
                            <p className="text-gray-600 mt-1">Manage your business accounts and team members</p>
                        </div>
                        <button
                            onClick={() => setShowForm(!showForm)}
                            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                        >
                            <Plus size={18} />
                            New Organisation
                        </button>
                    </div>

                    {/* Error Alert */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex gap-3">
                            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-red-800">Error</h3>
                                <p className="text-red-700 text-sm">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Create Form */}
                    {showForm && (
                        <div className="bg-white rounded-lg shadow p-6 mb-8">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4">Create New Organisation</h2>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Organisation Name"
                                        required
                                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Email"
                                        required
                                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="Phone"
                                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                    <input
                                        type="text"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleChange}
                                        placeholder="City"
                                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                    <input
                                        type="text"
                                        name="postcode"
                                        value={formData.postcode}
                                        onChange={handleChange}
                                        placeholder="Postcode"
                                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                    <input
                                        type="text"
                                        name="sector"
                                        value={formData.sector}
                                        onChange={handleChange}
                                        placeholder="Sector (e.g., Letting Agent)"
                                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                    <select
                                        name="subscription_tier"
                                        value={formData.subscription_tier}
                                        onChange={handleChange}
                                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="free">Free</option>
                                        <option value="starter">Starter</option>
                                        <option value="professional">Professional</option>
                                        <option value="enterprise">Enterprise</option>
                                    </select>
                                    <select
                                        name="max_users"
                                        value={formData.max_users}
                                        onChange={handleChange}
                                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="5">Max Users: 5</option>
                                        <option value="10">Max Users: 10</option>
                                        <option value="25">Max Users: 25</option>
                                        <option value="50">Max Users: 50</option>
                                        <option value="100">Max Users: 100</option>
                                    </select>
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {submitting ? 'Creating...' : 'Create Organisation'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Organisations Grid */}
                    {organisations.length === 0 ? (
                        <div className="bg-white rounded-lg shadow p-12 text-center">
                            <Building2 size={48} className="mx-auto text-gray-400 mb-4" />
                            <p className="text-gray-600 mb-4">No organisations yet</p>
                            <button
                                onClick={() => setShowForm(true)}
                                className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                            >
                                <Plus size={18} />
                                Create Your First Organisation
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {organisations.map((org) => (
                                <div key={org.id} className="bg-white rounded-lg shadow p-6">
                                    <div className="flex items-start justify-between mb-4">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">{org.name}</h3>
                                            <p className="text-gray-600 text-sm">{org.sector || 'No sector'}</p>
                                        </div>
                                        <div className="bg-blue-100 p-3 rounded-lg">
                                            <Building2 className="text-blue-600" />
                                        </div>
                                    </div>

                                    <div className="space-y-2 mb-4 text-sm text-gray-600">
                                        <p><strong>Email:</strong> {org.email}</p>
                                        {org.city && <p><strong>Location:</strong> {org.city}</p>}
                                        <p><strong>Plan:</strong> {org.subscription_tier}</p>
                                        <p className="flex items-center gap-1">
                                            <Users size={16} />
                                            <strong>Max Users:</strong> {org.max_users}
                                        </p>
                                    </div>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleDelete(org.id)}
                                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded text-sm font-medium"
                                        >
                                            <Trash2 size={16} />
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
